import { useContext, useRef } from "react";
import { Context } from "../../../../context/Context";
import { ActionMenu, Archive, ArchiveRestore, Building2, Button, CheckCircle, ClipboardList, CloudDownload, Copy, History, Pencil, RestaurantBar, Upload } from "../../../ui";
import { LIFECYCLE_LABELS } from "../../menuRole";
import { menuActionsText } from "../../menuActionsText";

// Tabs = the restaurant's menus. The one state-changing action that fits the
// menu's lifecycle (activate a draft / restore an archived menu) is a button;
// everything else lives in the "⋯" menu and only lists what can succeed.
export default function FoodTopbar({ menus, menu, role, onSelectMenu, panel, onOpenPanel, onExport, onImportFile, onRename, onActivate, onArchive, onRestore, onDuplicate, onHistory, busy }) {
  const { lang } = useContext(Context);
  const t = menuActionsText(lang);
  const fileRef = useRef(null);
  const importMode = useRef("replace");
  const archived = menu.lifecycle === "archived";
  const editable = role.canWrite && !archived;
  const activeIndex = Math.max(0, menus.findIndex((m) => String(m._id) === String(menu._id)));

  const items = [
    { key: "rename", label: t.rename, icon: <Pencil size={16} />, onClick: onRename, hidden: !editable },
    { key: "duplicate", label: t.duplicate, icon: <Copy size={16} />, onClick: onDuplicate, hidden: !role.canWrite },
    { key: "branches", label: t.manageBranches, icon: <Building2 size={16} />, onClick: () => onOpenPanel("branches"), hidden: archived || !role.canWrite },
    { key: "additions", label: t.additions, icon: <ClipboardList size={16} />, onClick: () => onOpenPanel("additions"), hidden: !role.canViewAdditions },
    { key: "archived", label: t.archivedContent, icon: <ArchiveRestore size={16} />, onClick: () => onOpenPanel("archived"), hidden: !editable },
    { key: "history", label: t.viewHistory, icon: <History size={16} />, onClick: onHistory, divider: true },
    { key: "export", label: t.exportExcel, icon: <CloudDownload size={16} />, onClick: onExport },
    { key: "import", label: t.importFile, icon: <Upload size={16} />, onClick: () => { importMode.current = "replace"; fileRef.current?.click(); }, hidden: !editable },
    { key: "importAppend", label: t.importAppend, icon: <Upload size={16} />, onClick: () => { importMode.current = "append"; fileRef.current?.click(); }, hidden: !editable },
    { key: "archive", label: t.archiveMenu, icon: <Archive size={16} />, onClick: onArchive, tone: "danger", divider: true, hidden: !editable },
    { key: "restore", label: t.restoreMenu, icon: <ArchiveRestore size={16} />, onClick: onRestore, hidden: !(archived && role.canWrite) },
  ].map((item) => ({ ...item, disabled: busy && item.key !== "history" && item.key !== "export" }));

  return (
    <RestaurantBar
      restaurants={menus}
      activeRestaurant={activeIndex}
      onRestaurantChange={(index, entry) => onSelectMenu(entry._id)}
      getRestaurantKey={(entry) => entry._id}
      getRestaurantLabel={(entry) => `${entry.name} · ${LIFECYCLE_LABELS[lang]?.[entry.lifecycle] || entry.lifecycle}`}
      getRestaurantStatus={(entry) => ({
        hasYellowMiss: entry.lifecycle === "draft",
        hasGrayMiss: (entry.counts?.itemsWithoutImage || 0) > 0,
      })}
      actions={
        <div className="food-top-actions">
          {editable && menu.lifecycle === "draft" && (
            <Button type="button" variant="success" appearance="filled" size="sm" rounded icon={<CheckCircle size={16} />} disabled={busy} onClick={onActivate}>
              {t.activate}
            </Button>
          )}
          {archived && role.canWrite && (
            <Button type="button" variant="primary" appearance="filled" size="sm" rounded icon={<ArchiveRestore size={16} />} disabled={busy} onClick={onRestore}>
              {t.restore}
            </Button>
          )}
          {panel && panel !== "history" && (
            <Button type="button" variant="primary" appearance="soft" size="sm" rounded onClick={() => onOpenPanel(null)}>
              {t.close}: {{ branches: t.manageBranches, additions: t.additions, archived: t.archivedContent }[panel]}
            </Button>
          )}
          <ActionMenu label={t.menuActions} items={items} />
          <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={(event) => onImportFile(event, importMode.current)} aria-label={t.importFile} />
        </div>
      }
    />
  );
}
